import { useEffect, useMemo, useRef } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Link from '@mui/material/Link'
import Skeleton from '@mui/material/Skeleton'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableFooter from '@mui/material/TableFooter'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import { useIntersectionObserver } from '@react-hookz/web'
import {
    type OnChangeFn,
    type RowSelectionState,
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table'
import dayjs from 'dayjs'
import LocalizedFormat from 'dayjs/plugin/localizedFormat'

import { useLanguage } from '~/common/hooks'
import { type Product } from '~/common/schemas/product'
import { useAppSelector } from '~/store'
import { getTranslation } from '~/tools/utils'
import { getPath } from '~/utils'
import ChannelIcons from './channel-icons'

dayjs.extend(LocalizedFormat)

const ProductTableCell = styled(TableCell)(({ theme }) => ({
    py: 0,
    px: 1.5,

    '&.MuiTableCell-stickyHeader': {
        backgroundColor: theme.palette.common.white,
    },
}))

const ProductImg = styled('img')(() => ({
    aspectRatio: '1/1',
    my: 0.5,
    borderRadius: 5,
}))

const skeletonRows = Array.from({ length: 25 }) as Product[]
const helper = createColumnHelper<Product>()

type Props = {
    status: 'loading' | 'success' | 'error'
    rowSelection: RowSelectionState
    isFetchingMore: boolean
    canFetchMore?: boolean
    products?: Product[]
    stickyHeader?: boolean
    onRowSelectionChange: OnChangeFn<RowSelectionState>
    onStatusUpdate: (productId: Product['id'], status: Product['status']) => void
    onFetchMore: () => void
}

export default function ProductsTable({
    status,
    products,
    rowSelection,
    canFetchMore,
    isFetchingMore,
    stickyHeader,
    onRowSelectionChange,
    onStatusUpdate,
    onFetchMore,
}: Props) {
    const language = useLanguage()
    const staticRoot = useAppSelector((state) => state.initial.staticRoot)

    const ref = useRef<HTMLTableSectionElement>(null)
    const entry = useIntersectionObserver(ref)
    useEffect(() => {
        if (entry?.isIntersecting) {
            onFetchMore()
        }
    }, [entry?.isIntersecting, onFetchMore])

    const isFetchingAny = status === 'loading' || isFetchingMore
    const columns = useMemo(
        () => [
            helper.display({
                id: 'select',
                header: ({ table }) => (
                    <Checkbox
                        disabled={status === 'loading' || products?.length === 0}
                        checked={table.getIsAllRowsSelected()}
                        indeterminate={table.getIsSomeRowsSelected()}
                        onChange={table.getToggleAllRowsSelectedHandler()}
                    />
                ),
                cell: ({ row }) => (
                    <Checkbox
                        disabled={!row.original}
                        checked={row.getIsSelected()}
                        onChange={row.getToggleSelectedHandler()}
                    />
                ),
                size: 40,
            }),
            helper.accessor('cover_image_url', {
                header: '',
                cell: ({ getValue, row }) => {
                    const product = row.original
                    if (product) {
                        return (
                            <ProductImg
                                src={getValue()}
                                alt={getTranslation(product, language, 'name')}
                                height={50}
                            />
                        )
                    }

                    return isFetchingAny ? <Skeleton height={70} width={50} /> : null
                },
                size: 50,
            }),
            helper.accessor('translations', {
                header: gettext('Name'),
                cell: ({ row }) => {
                    const product = row.original
                    if (product) {
                        return (
                            <Link
                                component={RouterLink}
                                sx={{ textDecoration: 'none', color: 'inherit' }}
                                to={getPath('product', {
                                    product_id: product.id.toString(),
                                    store_id: product.store.id.toString(),
                                })}
                            >
                                <Typography noWrap variant="body2">
                                    {getTranslation(product, language, 'name')}
                                </Typography>
                            </Link>
                        )
                    }

                    return isFetchingAny ? <Skeleton height={36} width={400} /> : null
                },
                size: 600,
            }),
            helper.accessor('date_created', {
                header: gettext('Created'),
                cell: ({ getValue, row }) => {
                    const product = row.original
                    if (product) {
                        return (
                            <Typography noWrap variant="body2">
                                {dayjs(getValue()).format('lll')}
                            </Typography>
                        )
                    }

                    return isFetchingAny ? <Skeleton height={36} width={150} /> : null
                },
                size: 150,
            }),
            helper.accessor('channel_products', {
                header: gettext('Channels'),
                cell: ({ row }) => {
                    const channelProducts = row.original?.channel_products
                    if (channelProducts) {
                        return (
                            <ChannelIcons
                                channels={channelProducts.map((cp) => cp.shop.channel)}
                                hasErrors={channelProducts.some((cp) => !!cp.error_message)}
                            />
                        )
                    }

                    return isFetchingAny
                        ? Array.from({ length: 3 }).map((_, i) => (
                              <Skeleton
                                  key={i}
                                  variant="circular"
                                  height={20}
                                  width={20}
                                  style={{ display: 'inline-block', marginRight: 5 }}
                              />
                          ))
                        : null
                },
                size: 100,
            }),
            helper.accessor('status', {
                header: gettext('Active'),
                cell: ({ getValue, row }) => {
                    const product = row.original
                    if (product) {
                        return (
                            <Switch
                                color="secondary"
                                checked={getValue() === 'active'}
                                disabled={getValue() === 'deleted'}
                                onChange={(_, checked) => {
                                    onStatusUpdate(product.id, checked ? 'active' : 'inactive')
                                }}
                            />
                        )
                    }

                    return isFetchingAny ? <Switch disabled checked /> : null
                },
                size: 60,
            }),
        ],
        [isFetchingAny, language, onStatusUpdate, products?.length, status]
    )

    const data = useMemo(() => {
        if (status === 'loading') {
            return skeletonRows
        }

        if (!products) {
            return []
        }

        if (isFetchingMore) {
            return [...products, ...skeletonRows]
        }

        return products
    }, [isFetchingMore, products, status])

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        state: { rowSelection },
        onRowSelectionChange,
    })

    return (
        <Table stickyHeader={stickyHeader} sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead sx={{ borderTop: (theme) => `1px solid ${theme.palette.grey[200]}` }}>
                {table.getHeaderGroups().map((group) => (
                    <TableRow key={group.id}>
                        {group.headers.map((header) => (
                            <TableCell
                                key={header.id}
                                style={{ width: header.getSize() }}
                                sx={{
                                    py: 0,
                                    px: 1.5,

                                    '&.MuiTableCell-stickyHeader': {
                                        backgroundColor: (theme) => theme.palette.common.white,
                                    },
                                }}
                            >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableHead>

            <TableBody>
                {status === 'error' && (
                    <TableRow>
                        <TableCell colSpan={columns.length}>
                            <Typography
                                align="center"
                                color="error"
                                variant="subtitle1"
                                sx={{ p: 2 }}
                            >
                                {gettext('Failed to load products')}
                            </Typography>
                        </TableCell>
                    </TableRow>
                )}
                {products?.length === 0 ? (
                    <TableRow>
                        <ProductTableCell
                            colSpan={columns.length}
                            sx={{
                                textAlign: 'center',
                                padding: 10,
                                color: (theme) => theme.palette.grey[400],
                            }}
                        >
                            <img src={staticRoot + 'web/img/product-gray.png'} />
                            <Typography variant="subtitle1">
                                {gettext('No products to display')}
                            </Typography>
                        </ProductTableCell>
                    </TableRow>
                ) : (
                    table.getRowModel().rows.map((row) => (
                        <TableRow hover key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                                <ProductTableCell key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </ProductTableCell>
                            ))}
                        </TableRow>
                    ))
                )}
            </TableBody>

            {/* Manually set display here to bind ref */}
            <TableFooter
                ref={ref}
                style={{ display: products?.length ? 'table-footer-group' : 'none' }}
            >
                <TableRow>
                    <TableCell align="center" colSpan={table.getVisibleFlatColumns().length}>
                        {canFetchMore ? (
                            <Button size="small" disabled={isFetchingAny} onClick={onFetchMore}>
                                Fetch more
                            </Button>
                        ) : (
                            <Typography variant="button">{gettext('End of list')}</Typography>
                        )}
                    </TableCell>
                </TableRow>
            </TableFooter>
        </Table>
    )
}
